/* eslint-disable @typescript-eslint/indent */
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import React, { useContext } from 'react';
import { Summary as OrderSummary, StepName } from '|/app/services';
import * as Summary from '|/app/steps/order-summary/order-summary-components.styles';
import * as Total from '|/app/steps/order-summary/sections/cost-breakdown-section/cost-breakdown-section.styles';
import currency from '|/helpers/currency';
import useOrderSummaryVm from '|/helpers/use-order-summary';
import SummaryContext from '../../../../summary-context';
import useNavigateToStep from '../../../use-navigate-to-step';

const defaultProps = {
  summary: {} as OrderSummary
};

const CostBreakdownSection = ({ summary } = defaultProps) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const { setShowSummary } = useContext(SummaryContext);
  const navigateToStep = useNavigateToStep();
  const {
    isCash,
    showPaymentDetails,
    showDeliveryFee,
    stateName,
    hasAncillaryProducts,
    declinedAncillary,
    collapsedFees,
    setCollapsedFees,
    tradeDeclined,
    completedTrade,
    calculatedTradeValue,
    calculatedTotalRebates,
    tradeMessage,
    totalTaxes,
    totalFeesAmount: totalFees,
    totalDiscountsAmount: totalDiscounts,
    tradeInTaxSavings,
    cashSubtotal,
    totalFinanced,
    cashDownAmount,
    showTradeInEdit,
    showProtectionEdit,
    isPaymentBreakdownCollapsed,
    setIsPaymentBreakdownCollapsed,
    shippingFee,
    deliveryCharge,
    gapStatus,
    gapPrice,
    productStatus,
    productPrice,
    productType,
    tradeInStatus,
    tradeInYear: year,
    tradeInMake: make,
    tradeInModel: model,
    tradeInValue: value,
    lienAmount,
    totalPrice,
    cashDown,
    creditFromPreviousOrder,
    thirdPartyCash,
    vehicleStickerPrice,
    feesList,
    dueAmount,
    dueLabel,
    hasEvTaxCredit,
    evTaxCreditAmount,
    showEvTaxCredit,
    hasSelectedEvTaxCreditOption
  } = useOrderSummaryVm(summary);

  return (
    <Summary.Section>
      <Summary.Heading>Cost Breakdown</Summary.Heading>
      <Summary.Box>
        <Summary.List>
          <Summary.SummaryListItem
            label="Carvana Price"
            value={currency(vehicleStickerPrice || 0)}
          />
          <Summary.SummaryListItem label="Tax" value={currency(totalTaxes)} />
          {(tradeInTaxSavings || 0) > 0 && !tradeDeclined && completedTrade && (
            <Summary.TaxSavingsSection>
              <Summary.TaxSavingsText>
                Includes ${Math.ceil(tradeInTaxSavings || 0)} in {stateName}{' '}
                sales tax savings for trading in your vehicle.
              </Summary.TaxSavingsText>
            </Summary.TaxSavingsSection>
          )}
          <Summary.SummaryListItem
            label="Total title and registration"
            value={currency(totalFees)}
          />
          {!collapsedFees && (
            <Summary.Sublist>
              {feesList?.map(x =>
                x.description ? (
                  <Summary.SummaryListItem
                    key={x.description}
                    label={x.description}
                    value={currency(x.amount || 0)}
                  />
                ) : null
              )}
            </Summary.Sublist>
          )}
          <Summary.ShowHidewrapper
            onClick={() => setCollapsedFees(!collapsedFees)}
            data-analytics-id="pp_order_summary_fees_toggle"
          >
            <Summary.Arrow expanded={collapsedFees.toString()} />
            <Summary.ShowHideText>
              {collapsedFees ? 'Show' : 'Hide'} details
            </Summary.ShowHideText>
          </Summary.ShowHidewrapper>
          {totalDiscounts > 0 && (
            <Summary.SummaryListItem
              label="Total Reductions"
              value={currency(totalDiscounts)}
            />
          )}
          <Summary.SummaryListItem
            label="Shipping  (Nonrefundable)"
            value={currency(shippingFee || 0) || '0'}
            listItemClass="shipping-delivery-container"
          />
          <Summary.DividerText3>
            Shipping the car to a location near you
          </Summary.DividerText3>
          {showDeliveryFee && (
            <>
              <Summary.SummaryListItem
                label="Delivery"
                value={currency(deliveryCharge || 0)}
                listItemClass="shipping-delivery-container"
              />
              <Summary.DividerText3>
                Delivering the car from our location to you
              </Summary.DividerText3>
            </>
          )}
          <Summary.SummaryListItem label="Dealer Fees" value="NEVER" isBold />
          <Summary.SummaryListItem label="Hidden Fees" value="NEVER" isBold />
        </Summary.List>
      </Summary.Box>
      {hasAncillaryProducts && (
        <Summary.Box>
          <Summary.List>
            {gapStatus !== null && gapStatus !== 'NotSet' ? (
              <Summary.SummaryListItem
                label="GAP Coverage"
                value={
                  gapStatus === 'Declined'
                    ? 'Declined'
                    : currency(gapPrice || 0)
                }
              />
            ) : null}
            {productStatus !== null && productStatus !== 'NotSet' ? (
              <Summary.SummaryListItem
                label={
                  <span>
                    CarvanaCare<sup>®</sup>
                    {productStatus === 'Applied' && ` ${productType}`}
                  </span>
                }
                value={
                  productStatus === 'Declined'
                    ? 'Declined'
                    : currency(productPrice || 0)
                }
              />
            ) : null}
          </Summary.List>
          {showProtectionEdit && declinedAncillary && (
            <Summary.EditButtonRightAligned
              onClick={() => {
                trackEvent('review order vsc clicked');
                setShowSummary(false);
                navigateToStep(StepName.VSC);
              }}
              data-analytics-id="pp_order_summary_carvanacare_edit"
            >
              Add CarvanaCare<sup>®</sup>
            </Summary.EditButtonRightAligned>
          )}

          {showProtectionEdit && !declinedAncillary && (
            <Summary.EditButton
              onClick={() => {
                setShowSummary(false);
                navigateToStep(StepName.VSC);
              }}
              data-analytics-id="pp_order_summary_protection_edit"
            >
              Edit Protection Details
            </Summary.EditButton>
          )}
        </Summary.Box>
      )}
      <Summary.Box>
        <Total.Wrapper>
          <Total.LabelWrapper>
            <Summary.BoxLabel>Subtotal</Summary.BoxLabel>
            <Summary.BoxText>(Including taxes, fees, upgrades)</Summary.BoxText>
          </Total.LabelWrapper>
          <Total.Value>
            {totalPrice
              ? currency(
                  totalPrice + calculatedTradeValue + calculatedTotalRebates
                )
              : null}
          </Total.Value>
        </Total.Wrapper>
        {showEvTaxCredit && (
          <>
            <Summary.EvTaxCreditBox
              hasSelectedOption={hasSelectedEvTaxCreditOption}
            >
              <Total.EvWrapper>
                <Total.EvLabelWrapper>
                  <Summary.EvLabel hasEvTaxCredit={hasEvTaxCredit || false}>
                    Clean Vehicle Tax Credit:
                  </Summary.EvLabel>
                </Total.EvLabelWrapper>
                {hasEvTaxCredit ? (
                  <Total.GreenValue>
                    {currency(evTaxCreditAmount || 0)}
                  </Total.GreenValue>
                ) : (
                  <Total.BoxTextRight>
                    {hasSelectedEvTaxCreditOption
                      ? 'Not Applied'
                      : '* We still need your tax credit decision'}
                  </Total.BoxTextRight>
                )}
              </Total.EvWrapper>
              {hasSelectedEvTaxCreditOption && (
                <>
                  {!hasEvTaxCredit && (
                    <Summary.EvDividerText>
                      If you’re eligible and plan to claim this credit on your
                      tax return, you’ll need to provide your SSN/ITIN after
                      placing your order.
                    </Summary.EvDividerText>
                  )}
                  <Summary.EditButton
                    onClick={() => {
                      setShowSummary(false);
                      navigateToStep(StepName.CleanVehicleTaxCredit);
                    }}
                    data-analytics-id="pp_order_summary_taxcredit_edit"
                  >
                    Edit Tax Credit Selection
                  </Summary.EditButton>
                </>
              )}
            </Summary.EvTaxCreditBox>
            {hasSelectedEvTaxCreditOption && <Summary.DividerLine />}
          </>
        )}
        <Summary.List>
          {completedTrade ? (
            <Summary.SummaryListItem
              label="Trade-In"
              value={
                tradeInStatus === 'Declined'
                  ? 'Declined'
                  : `${year} ${make} ${model}`
              }
            />
          ) : (
            <Summary.SummaryListItem
              label="Trade-In"
              value="* We still need your trade-in decision"
            />
          )}
          {!tradeDeclined && completedTrade && (
            <>
              <Summary.SummaryListItem
                label="Your Carvana offer"
                value={currency(value || 0)}
              />
              <Summary.SummaryListItem
                label="Trade-in loan balance"
                value={currency((lienAmount || 0) * -1)}
              />
            </>
          )}
        </Summary.List>
      </Summary.Box>
      {!tradeDeclined && completedTrade && (
        <Summary.Box>
          <Summary.SummaryListItem
            label={
              calculatedTradeValue >= 0 ? 'Trade-in value' : 'Amount you owe'
            }
            value={currency(calculatedTradeValue)}
          />
          <Summary.DividerText2>{tradeMessage}</Summary.DividerText2>
        </Summary.Box>
      )}

      {showTradeInEdit && (
        <Summary.Box>
          <Summary.EditButton
            onClick={() => {
              setShowSummary(false);
              navigateToStep(StepName.TradeIn);
            }}
            data-analytics-id="pp_order_summary_tradein_edit"
          >
            {tradeDeclined ? 'Add a Trade-in' : 'Edit Trade-In Details'}
          </Summary.EditButton>
        </Summary.Box>
      )}

      <Summary.Box>
        <Total.Wrapper>
          <Total.LabelWrapper>
            <Summary.BoxLabel>Total Price</Summary.BoxLabel>
            <Summary.BoxText>
              (Including taxes, fees, upgrades, trade-in)
            </Summary.BoxText>
          </Total.LabelWrapper>
          <Total.Value>{currency(totalPrice || 0)}</Total.Value>
        </Total.Wrapper>
        {showPaymentDetails && (
          <Total.DueAtOrderWrapper>
            <Total.Wrapper>
              <Total.LabelWrapper>
                <Summary.BoxLabel>{dueLabel}</Summary.BoxLabel>
              </Total.LabelWrapper>
              <Total.Value>{currency(dueAmount || 0)}</Total.Value>
            </Total.Wrapper>
            {!isPaymentBreakdownCollapsed && (
              <Total.PaymentBreakdownList>
                {(cashDown || 0) > 0 && !isCash ? (
                  <Summary.SummaryListItem
                    label="Cash down payment"
                    value={currency(cashDown || 0)}
                  />
                ) : null}
                {isCash && (
                  <Summary.SummaryListItem
                    label={shippingFee ? 'Subtotal' : 'Total vehicle price'}
                    value={currency(cashSubtotal)}
                  />
                )}
                {(shippingFee || 0) > 0 ? (
                  <Summary.SummaryListItem
                    label="Shipping (Nonrefundable)"
                    value={currency(shippingFee || 0)}
                  />
                ) : null}
                {(creditFromPreviousOrder || 0) > 0 ? (
                  <Summary.SummaryListItem
                    label="Credit from previous order"
                    value={`-${currency(creditFromPreviousOrder || 0)}`}
                  />
                ) : null}
              </Total.PaymentBreakdownList>
            )}

            {((cashDownAmount || 0) > 0 ||
              (shippingFee || 0) > 0 ||
              (creditFromPreviousOrder || 0) > 0 ||
              (thirdPartyCash || 0) > 0) && (
              <Total.PaymentBreakdownShowHideWrapper
                onClick={() =>
                  setIsPaymentBreakdownCollapsed(!isPaymentBreakdownCollapsed)
                }
                data-analytics-id="pp_order_summary_payment_toggle"
              >
                <Summary.Arrow
                  expanded={isPaymentBreakdownCollapsed.toString()}
                />
                <Summary.ShowHideText>
                  {isPaymentBreakdownCollapsed ? 'Show' : 'Hide'} details
                </Summary.ShowHideText>
              </Total.PaymentBreakdownShowHideWrapper>
            )}
          </Total.DueAtOrderWrapper>
        )}
        <Total.Wrapper>
          <Total.LabelWrapper>
            <Summary.BoxLabel>Total Financed</Summary.BoxLabel>
          </Total.LabelWrapper>
          <Total.Value>{currency(totalFinanced)}</Total.Value>
        </Total.Wrapper>
      </Summary.Box>
    </Summary.Section>
  );
};

export default CostBreakdownSection;
